'use client';

import { initialize, updateParentScrollOnResize } from '@open-iframe-resizer/core';
import dynamic from 'next/dynamic';
import { useRef, useEffect } from 'react';
import { ComponentSection } from '@/app/_components/core/component-section';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useHtmlWithScripts } from '@/app/_lib/use-html-with-scripts';
import { HtmlBlockComponentProps } from './html-block.types';

// Custom form handler - loaded from @custom/_exports registry
// Allows project-specific form handling (e.g. Dynamics 365, HubSpot)
const HtmlBlockFormHandler = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.HtmlBlockFormHandler,
    })),
  { ssr: false }
);

/**
 * HtmlBlockComponent renders raw HTML from the CMS.
 *
 * This component handles:
 * 1. Parsing HTML content with html-react-parser
 * 2. Dynamically loading external scripts (with cache-busting for client-side navigation)
 * 3. Executing inline scripts on every mount
 * 4. Lazy loading scripts - only loads when the section becomes visible
 * 5. Auto-resizing iframes via @open-iframe-resizer/core (no child script needed for same-origin)
 *
 * Uses the shared useHtmlWithScripts hook for consistent script handling
 * across form-embed and html-block components.
 */
export function HtmlBlockComponent({
  component,
  componentPosition,
  isNested,
}: HtmlBlockComponentProps) {
  const { html, intro } = component;
  const containerRef = useRef<HTMLElement>(null);
  const { content: parsedContent } = useHtmlWithScripts(html, {
    lazy: true,
    lazyRootMargin: '200px',
    containerRef,
  });

  // Auto-resize iframes within the HTML block, re-initialize on iframe navigation
  useEffect(() => {
    if (!containerRef.current) return;
    const iframes = containerRef.current.querySelectorAll('iframe');
    if (iframes.length === 0) return;

    let cleanups: Awaited<ReturnType<typeof initialize>>[] = [];
    const abortControllers: AbortController[] = [];

    const unsubscribeAll = () => {
      cleanups.flat().forEach((instance) => { instance.unsubscribe(); });
    };

    const initIframe = (iframe: HTMLIFrameElement) => {
      // updateParentScrollOnResize keeps the iframe's viewport position stable
      // when its height changes, so form step/submit actions don't jump the page
      // back to the top.
      initialize({ onIframeResize: updateParentScrollOnResize }, iframe)
        .then((result) => {
          cleanups.push(result);
        })
        .catch((error: unknown) => {
          // Resizer init failed — surface it instead of a silently dead iframe.
          console.error('html-block iframe resizer failed to initialize:', error);
        });
    };

    Array.from(iframes).forEach((iframe) => {
      initIframe(iframe);

      // Re-initialize resizer when iframe navigates (e.g. form submission redirect)
      const ac = new AbortController();
      abortControllers.push(ac);
      iframe.addEventListener(
        'load',
        () => {
          unsubscribeAll();
          cleanups = [];
          initIframe(iframe);
        },
        { signal: ac.signal },
      );
    });

    return () => {
      unsubscribeAll();
      abortControllers.forEach((ac) => { ac.abort(); });
    };
  }, [parsedContent]);

  if (!html) {
    return null;
  }

  return (
    <ComponentSection isNested={isNested}
      ref={containerRef}
      {...getSectionProps(component, componentPosition, isNested)}
      className="container py-section-padding-y-mobile xl:py-section-padding-y-desktop"
    >
      {intro && (
        <SectionIntro intro={intro} trackingPrefix={getTrackingPrefix(component)} />
      )}
      <div>{parsedContent}</div>
      <HtmlBlockFormHandler html={html} containerRef={containerRef} />
    </ComponentSection>
  );
}
